/**
 * Wellbeing & Safety pack (docs/value-added-plan-2026-11.md item 1):
 * backend/app/api/routes/wellbeing.py. The server decides who sees what; this
 * file only describes the shapes.
 */
import { api } from "../../services/api";

export interface Helpline {
    label: string;
    number: string;
    hours: string;
}

export interface StaffRef {
    id: string;
    full_name: string;
}

export interface WellbeingSettings {
    helplines: Helpline[];
    counsellor_ids: string[];
    team_ids: string[];
    checkins_enabled: boolean;
    referral_contact: string;
}

export interface WellbeingSettingsView extends WellbeingSettings {
    counsellors: StaffRef[];
    team: StaffRef[];
    learner_count: number;
}

export interface MyWellbeing {
    helplines: Helpline[];
    checkins_enabled: boolean;
    can_check_in: boolean;
    reason: string;
    checked_in_today: number;
    is_counsellor: boolean;
}

export type CheckinStatus = "new" | "seen" | "referred";

export interface Checkin {
    id: string;
    created_at: string;
    student_id: string;
    student_name: string;
    mood: number;
    note: string;
    urgent: boolean;
    status: CheckinStatus;
}

export type ReferralCategory = "stress" | "anxiety" | "low_mood" | "self_harm_risk" | "bullying" | "family" | "academic" | "other";
export type ReferralStatus = "open" | "follow_up" | "closed";

export interface Referral {
    id: string;
    created_at: string;
    updated_at: string;
    student_id: string;
    student_name: string;
    category: ReferralCategory;
    action: string;
    referred_to: string;
    follow_up_on: string | null;
    status: ReferralStatus;
    created_by_name: string;
}

export interface AnnualWellnessReport {
    organisation: string;
    year_label: string;
    period_from: string;
    period_to: string;
    learners: number;
    counsellors: string[];
    team: string[];
    helplines_displayed: number;
    checkins: number;
    checkins_by_mood: Record<string, number>;
    urgent_checkins: number;
    referrals: number;
    referrals_by_category: Record<string, number>;
    referrals_by_status: Record<string, number>;
    wellbeing_trainings: number;
    staff_trained_twice: number;
    staff_total: number;
    generated_at: string;
}

export const MOODS: { value: number; face: string; label: string }[] = [
    { value: 1, face: "😢", label: "Very low" },
    { value: 2, face: "🙁", label: "Low" },
    { value: 3, face: "😐", label: "Okay" },
    { value: 4, face: "🙂", label: "Good" },
    { value: 5, face: "😄", label: "Great" }
];

export const CATEGORY_LABEL: Record<ReferralCategory, string> = {
    stress: "Stress",
    anxiety: "Anxiety",
    low_mood: "Low mood",
    self_harm_risk: "Risk of self-harm",
    bullying: "Bullying",
    family: "Family",
    academic: "Academic",
    other: "Other"
};

const wellbeingApi = api.enhanceEndpoints({ addTagTypes: ["Wellbeing", "WellbeingDesk"] }).injectEndpoints({
    endpoints: (builder) => ({
        getMyWellbeing: builder.query<MyWellbeing, void>({
            query: () => ({ url: "/wellbeing/me" }),
            providesTags: ["Wellbeing"]
        }),
        getPublicHelplines: builder.query<Helpline[], string>({
            query: (coachingId) => ({ url: `/wellbeing/public/${encodeURIComponent(coachingId)}/helplines` })
        }),
        getWellbeingSettings: builder.query<WellbeingSettingsView, void>({
            query: () => ({ url: "/wellbeing/settings" }),
            providesTags: ["Wellbeing"]
        }),
        saveWellbeingSettings: builder.mutation<WellbeingSettingsView, WellbeingSettings>({
            query: (body) => ({ url: "/wellbeing/settings", method: "PUT", body }),
            invalidatesTags: ["Wellbeing"]
        }),
        checkIn: builder.mutation<Checkin, { mood: number; note: string }>({
            query: (body) => ({ url: "/wellbeing/checkins", method: "POST", body }),
            invalidatesTags: ["Wellbeing"]
        }),
        listCheckins: builder.query<Checkin[], void>({
            query: () => ({ url: "/wellbeing/checkins" }),
            providesTags: ["WellbeingDesk"]
        }),
        setCheckinStatus: builder.mutation<Checkin, { id: string; status: CheckinStatus }>({
            query: ({ id, status }) => ({ url: `/wellbeing/checkins/${id}`, method: "PATCH", body: { status } }),
            invalidatesTags: ["WellbeingDesk"]
        }),
        searchLearners: builder.query<StaffRef[], string>({
            query: (q) => ({ url: "/wellbeing/learners", params: { q } })
        }),
        listReferrals: builder.query<Referral[], void>({
            query: () => ({ url: "/wellbeing/referrals" }),
            providesTags: ["WellbeingDesk"]
        }),
        addReferral: builder.mutation<Referral, { student_id: string; category: ReferralCategory; action: string; referred_to: string; follow_up_on: string | null; status: ReferralStatus }>({
            query: (body) => ({ url: "/wellbeing/referrals", method: "POST", body }),
            invalidatesTags: ["WellbeingDesk"]
        }),
        updateReferral: builder.mutation<Referral, { id: string; status: ReferralStatus }>({
            query: ({ id, ...body }) => ({ url: `/wellbeing/referrals/${id}`, method: "PATCH", body }),
            invalidatesTags: ["WellbeingDesk"]
        }),
        getWellnessReport: builder.query<AnnualWellnessReport, string | void>({
            query: (year) => ({ url: "/wellbeing/report", params: year ? { year } : undefined })
        })
    })
});

export const {
    useGetMyWellbeingQuery,
    useGetPublicHelplinesQuery,
    useGetWellbeingSettingsQuery,
    useSaveWellbeingSettingsMutation,
    useCheckInMutation,
    useListCheckinsQuery,
    useSetCheckinStatusMutation,
    useSearchLearnersQuery,
    useListReferralsQuery,
    useAddReferralMutation,
    useUpdateReferralMutation,
    useGetWellnessReportQuery
} = wellbeingApi;
